import PolicyEvaluationService from 'titus-ts/dist/js/Util/Policy/PolicyEvaluationService';
import PolicyEvaluationResult from 'titus-ts/dist/js/Util/Actions/PolicyEvaluationResult';
import DialogService from './dialogService';
import { STATE_PARAMS } from 'titus-ts/dist/js/Util/Policy/PolicyEvaluationBaseState';
import { TimeOutService } from './timeOutService';
import { CustomActionService } from './customActionService';
import { AuditService } from './auditing/baseAuditService';
import Action from 'titus-ts/dist/js/Util/Actions/Action';

export class PolicyService {
    private timeOutService: TimeOutService;
    private policyEvaluationService: PolicyEvaluationService;
    private dialogService: DialogService;
    private customActionService: CustomActionService;
    private auditService: AuditService | null;
    private ignoredPolicy = false;

    public constructor(
        timeOutService: TimeOutService,
        policyEvaluationService: PolicyEvaluationService,
        dialogService: DialogService,
        auditService: AuditService | null,
    ) {
        this.timeOutService = timeOutService;
        this.policyEvaluationService = policyEvaluationService;
        this.dialogService = dialogService;
        this.auditService = auditService;

        this.customActionService = new CustomActionService(timeOutService, dialogService);
    }

    public evaluatePolicy = async (): Promise<PolicyEvaluationResult> => {
        return new Promise<PolicyEvaluationResult>(async (resolve, reject) => {
            try {
                let result: PolicyEvaluationResult | undefined;
                let stateParams: Map<STATE_PARAMS, string> | undefined;

                if (this.policyEvaluationService.getEventType() === 'OnSend') {
                    this.timeOutService.subscribeTimeout(reject);
                }
                this.policyEvaluationService.resetState();

                while (!result || (result.status !== 'COMPLETED' && result.status !== 'BLOCKED')) {
                    result = await this.policyEvaluationService.evaluate(stateParams);
                    stateParams = await this.getUserInput(result);
                }
                if (result.status !== 'BLOCKED') {
                    const customActionSuccess = await this.customActionService.processCustomActions(result);
                    if (!customActionSuccess) result.status = 'BLOCKED';
                }

                if (result.status == 'BLOCKED') {
                    if (this.auditService) {
                        await this.auditService.auditEventBlocked(this.policyEvaluationService.getEventType());
                    }
                } else if (result.status == 'COMPLETED') {
                    if (this.auditService) {
                        await Promise.all([
                            this.auditActions(result.actions),
                            this.auditService.auditEventAllowed(
                                this.policyEvaluationService.getEventType(),
                                this.ignoredPolicy,
                            ),
                        ]);
                    }
                }

                resolve(result);
            } catch (err) {
                reject(err);
            } finally {
                this.timeOutService.cancelTimeout();
            }
        });
    };

    private getUserInput = async (result: PolicyEvaluationResult): Promise<Map<STATE_PARAMS, string> | undefined> => {
        let stateParams: Map<STATE_PARAMS, string> | undefined;
        if (result.status === 'REQUIRES_USER_ACTION') {
            const event = this.policyEvaluationService.getEventType();
            const dialogResult = await this.dialogService.showAlertsDialog(result.actions, event);
            if (dialogResult == 'DialogOk' || dialogResult == 'DialogOkIdCached') {
                this.ignoredPolicy = true;
            }
            stateParams = new Map<STATE_PARAMS, string>().set('USER_ACTION', dialogResult);
        } else if (result.status === 'INVALID_METADATA') {
            const dialogResult = await this.dialogService.showForceClassificationDialog();
            stateParams = new Map<STATE_PARAMS, string>().set('USER_ACTION', dialogResult);
        } else if (result.status !== 'BLOCKED' && result.status !== 'COMPLETED' && result.status !== 'IN_PROGRESS') {
            const dialogResult = await this.dialogService.showSuggestClassificationDialog(
                result.actions,
                result.status.actionId,
            );
            stateParams = new Map<STATE_PARAMS, string>()
                .set('USER_ACTION', dialogResult)
                .set('SUGGEST_HANDLED_ID', result.status.actionId);
        }
        return stateParams;
    };

    private auditActions = async (actions: Action[]): Promise<void> => {
        await Promise.all(actions.map((action) => this.auditAction(action)));
    };

    private auditAction = async (action: Action): Promise<void> => {
        if (!this.auditService) return;

        const actionType = action.type;
        const actionSeverity = this.getActionSeverity(action);

        await this.auditService.auditAction(this.policyEvaluationService.getEventType(), actionType, actionSeverity);
    };

    private getActionSeverity = (action: Action): string | undefined => {
        const actionSeverity = action.params.get('Severity');

        switch (actionSeverity) {
            case 'Warn':
            case 'Information':
            case 'Log Only':
            case 'Prevent':
                return actionSeverity;
        }

        return undefined;
    };
}
